import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { Branch, InventoryItem, Treatment, TransferLog } from '@/lib/types';
import {
  Banner,
  Card,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Pill,
  Stat,
  Tag,
  cellStyle,
  fieldStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';
import { useAuth } from '@/store/auth';

const FLAG: Record<string, string> = { TW: '🇹🇼', HK: '🇭🇰', SG: '🇸🇬', JP: '🇯🇵', CN: '🇨🇳', US: '🇺🇸' };
const flagOf = (c: string) => FLAG[c] ?? '🏳️';

const statusOf = (i: InventoryItem): 'out' | 'low' | 'ok' =>
  i.qty === 0 ? 'out' : i.qty <= i.lowAt ? 'low' : 'ok';

const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

export function InventoryPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const { msg, ok, fail } = useToast();

  const [scope, setScope] = useState<string>('all'); // 'all' | branchId
  const [cat, setCat] = useState<string>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [transferItem, setTransferItem] = useState<InventoryItem | null>(null);
  const [histOpen, setHistOpen] = useState(false);

  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/catalog/branches') });
  const items = useQuery({ queryKey: ['inv-items'], queryFn: () => api.get<InventoryItem[]>('/inventory/items') });
  const transfers = useQuery({ queryKey: ['inv-transfers'], queryFn: () => api.get<TransferLog[]>('/inventory/transfers') });
  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const rules = useQuery({ queryKey: ['deduction-rules'], queryFn: () => api.get<{ id: string; treatmentId: string; itemName: string; qty: number }[]>('/inventory/deduction-rules') });

  const branchList = branches.data ?? [];
  const itemList = items.data ?? [];
  const transferList = transfers.data ?? [];
  const treatmentList = treatments.data ?? [];

  const branchOf = (id: string) => branchList.find((b) => b.id === id);
  const branchName = (id: string) => branchOf(id)?.name ?? id;
  const treatmentName = (id: string | null) => (id ? treatmentList.find((t) => t.id === id)?.name ?? id : '');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['inv-items'] });
    void qc.invalidateQueries({ queryKey: ['inv-transfers'] });
  };

  // 國別分組（跨境組織結構）
  const countries = useMemo(() => {
    const map = new Map<string, Branch[]>();
    for (const b of branchList) {
      const arr = map.get(b.country) ?? [];
      arr.push(b);
      map.set(b.country, arr);
    }
    return [...map.entries()].map(([country, bs]) => ({ country, branches: bs }));
  }, [branchList]);
  const isCrossBorder = countries.length > 1;

  // 依分店範圍篩選
  const scopedItems = scope === 'all' ? itemList : itemList.filter((i) => i.branchId === scope);

  // 分類 + 計數
  const categoryTabs = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of scopedItems) {
      const c = i.category || '未分類';
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [scopedItems]);

  const visibleItems = scopedItems.filter((i) => cat === 'all' || (i.category || '未分類') === cat);

  // 採購建議（真實低水位資料）
  const reorderList = scopedItems
    .filter((i) => i.qty <= i.lowAt)
    .map((i) => ({ ...i, need: Math.max(i.lowAt - i.qty, 0) }))
    .sort((a, b) => a.qty - b.qty);

  const lowCount = scopedItems.filter((i) => i.qty <= i.lowAt && i.qty > 0).length;
  const outCount = scopedItems.filter((i) => i.qty === 0).length;

  const adjust = useMutation({
    mutationFn: (v: { itemId: string; delta: number }) => api.post('/inventory/items/adjust', v),
    onSuccess: () => { ok('庫存已調整'); refresh(); },
    onError: fail,
  });

  const sort = useSort(visibleItems, {
    name: (i) => i.name,
    category: (i) => i.category || '未分類',
    branch: (i) => branchName(i.branchId),
    qty: (i) => i.qty,
    expireAt: (i) => i.expireAt ?? '',
    bind: (i) => treatmentName(i.bindTreatmentId),
    status: (i) => ({ out: 0, low: 1, ok: 2 })[statusOf(i)],
  });

  return (
    <div>
      <PageHeader
        title="耗材庫存管理"
        subtitle="單店獨立庫存 · 同國可調撥 · 跨境受海關/法規限制 · 療程綁定自動扣減"
        right={
          isAdmin && (
            <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
              <Icon d={I.plus} size={13} /> 新增品項
            </button>
          )
        }
      />
      <Banner msg={msg} />

      {/* 分店範圍 */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <ScopeChip active={scope === 'all'} onClick={() => setScope('all')} label="🏢 全租戶合計" sub={`${itemList.length} 品項`} />
        {branchList.map((b) => (
          <ScopeChip
            key={b.id}
            active={scope === b.id}
            onClick={() => setScope(b.id)}
            label={`${flagOf(b.country)} ${b.name}`}
            sub={`${itemList.filter((i) => i.branchId === b.id).length} 品項 · ${b.region}`}
          />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="品項數" value={String(scopedItems.length)} tone="primary" />
        <Stat label="低庫存" value={String(lowCount)} tone="warn" />
        <Stat label="斷貨" value={String(outCount)} tone="danger" />
        <Stat label="調撥紀錄" value={String(transferList.length)} hint="點下方查看" />
      </div>

      {/* 跨境組織結構 + 採購建議 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="跨境組織結構" icon={<Icon d={I.globe} size={14} style={{ color: 'var(--primary)' }} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {countries.map(({ country, branches: bs }) => (
              <div key={country} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8 }}>
                <span style={{ fontSize: 18 }}>{flagOf(country)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{country} · {bs.map((b) => b.name).join('、')}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{bs.length} 家分店</div>
                </div>
                {bs.length > 1 ? <Pill bg="var(--success-bg)" color="var(--success)">互通調撥</Pill> : <Pill bg="var(--warn-bg)" color="var(--warn)">獨立經營</Pill>}
              </div>
            ))}
          </div>
          {isCrossBorder && (
            <div style={{ marginTop: 10, padding: 10, background: 'var(--danger-bg)', borderRadius: 8, fontSize: 11, color: 'var(--danger)' }}>
              <b>合規提示：</b>跨國分店耗材獨立庫存、不可直接調撥；注射針劑須通過當地 FDA 註冊代理商，藥證持有人不同無法跨境調撥。
            </div>
          )}
        </Card>

        <Card
          title="採購建議（補至低水位）"
          icon={<Icon d={I.alert} size={14} style={{ color: 'var(--warn)' }} />}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>品項</th>
                <th style={thStyle}>分店</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>現量</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>建議補貨</th>
              </tr>
            </thead>
            <tbody>
              {reorderList.map((i) => (
                <tr key={i.id}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>
                    {i.name}
                    {i.qty === 0 && <Tag tone="danger" style={{ marginLeft: 6 }}>斷貨</Tag>}
                  </td>
                  <td style={cellStyle}>{branchName(i.branchId)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{i.qty}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }} className="num">+{i.need}</td>
                </tr>
              ))}
              {reorderList.length === 0 && (
                <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>庫存皆高於低水位，無需補貨 👍</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      {/* 分類篩選 + 庫存清單 */}
      <Card
        title="耗材清單"
        right={
          <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setHistOpen(true)}>
            <Icon d={I.history} size={12} /> 調撥紀錄
          </button>
        }
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <CatTab active={cat === 'all'} onClick={() => setCat('all')} label={`全部 ${scopedItems.length}`} />
          {categoryTabs.map(([c, n]) => (
            <CatTab key={c} active={cat === c} onClick={() => setCat(c)} label={`${c} ${n}`} />
          ))}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="品名 / 規格" sortKey="name" sort={sort} />
              <SortTh label="分類" sortKey="category" sort={sort} />
              {scope === 'all' && <SortTh label="分店" sortKey="branch" sort={sort} />}
              <SortTh label="批號 / 效期" sortKey="expireAt" sort={sort} />
              <SortTh label="綁定療程" sortKey="bind" sort={sort} />
              <SortTh label="數量" sortKey="qty" sort={sort} align="right" />
              <SortTh label="狀態" sortKey="status" sort={sort} />
              <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((i) => {
              const st = statusOf(i);
              return (
                <tr key={i.id}>
                  <td style={cellStyle}>
                    <div style={{ fontWeight: 600 }}>{i.name}</div>
                    {(i.spec || i.brand) && <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{[i.brand, i.spec].filter(Boolean).join(' · ')}</div>}
                  </td>
                  <td style={cellStyle}>{i.category ? <Tag tone="tag">{i.category}</Tag> : <span style={{ color: 'var(--ink-4)' }}>未分類</span>}</td>
                  {scope === 'all' && <td style={cellStyle}>{flagOf(branchOf(i.branchId)?.country ?? '')} {branchName(i.branchId)}</td>}
                  <td style={cellStyle} className="num">
                    <div>{i.lot || '—'}</div>
                    <div style={{ fontSize: 10.5, color: i.expireAt ? 'var(--ink-3)' : 'var(--ink-4)' }}>{fmtDate(i.expireAt)}</div>
                  </td>
                  <td style={cellStyle}>{i.bindTreatmentId ? <Pill bg="var(--primary-tint)" color="var(--primary-deep)">{treatmentName(i.bindTreatmentId)}</Pill> : <span style={{ color: 'var(--ink-4)' }}>通用</span>}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700 }} className="num">{i.qty}<span style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 400 }}> {i.unit}</span></td>
                  <td style={cellStyle}>
                    {st === 'out' ? <Pill bg="var(--danger-bg)" color="var(--danger)">斷貨</Pill> : st === 'low' ? <Pill bg="var(--warn-bg)" color="var(--warn)">低庫存</Pill> : <Pill bg="var(--success-bg)" color="var(--success)">正常</Pill>}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => adjust.mutate({ itemId: i.id, delta: 10 })}>＋10</button>
                    <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => adjust.mutate({ itemId: i.id, delta: -1 })}>－1</button>
                    <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setTransferItem(i)} disabled={branchList.length < 2}>調撥</button>
                  </td>
                </tr>
              );
            })}
            {visibleItems.length === 0 && (
              <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={8}>此範圍 / 分類尚無耗材</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* 療程自動扣料規則 */}
      <DeductionRulesCard
        isAdmin={isAdmin}
        rules={rules.data ?? []}
        treatments={treatmentList}
        treatmentName={treatmentName}
        onCreated={() => void qc.invalidateQueries({ queryKey: ['deduction-rules'] })}
        ok={ok}
        fail={fail}
      />

      {addOpen && (
        <AddItemModal
          branches={branchList}
          treatments={treatmentList}
          existingCategories={categoryTabs.map(([c]) => c).filter((c) => c !== '未分類')}
          onClose={() => setAddOpen(false)}
          onSaved={() => { ok('品項已新增'); setAddOpen(false); refresh(); }}
          onFail={fail}
        />
      )}

      {transferItem && (
        <TransferModal
          item={transferItem}
          branches={branchList}
          branchOf={branchOf}
          onClose={() => setTransferItem(null)}
          onDone={(blocked, error) => {
            if (blocked) fail(new Error('跨境/庫存不足：調撥已被系統阻擋並記錄'));
            else if (error) fail(error);
            else ok('調撥完成');
            setTransferItem(null);
            refresh();
          }}
        />
      )}

      <Modal open={histOpen} onClose={() => setHistOpen(false)} width={620}>
        <ModalHeader title="調撥紀錄" sub="同國可調撥 · 跨境硬阻擋" onClose={() => setHistOpen(false)} />
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <Stat label="已送達" value={String(transferList.filter((t) => t.status === 'DELIVERED').length)} tone="success" />
            <Stat label="運送中" value={String(transferList.filter((t) => t.status === 'IN_TRANSIT').length)} tone="warn" />
            <Stat label="已阻擋" value={String(transferList.filter((t) => t.status === 'BLOCKED').length)} tone="danger" />
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>品名</th><th style={thStyle}>路線</th><th style={{ ...thStyle, textAlign: 'right' }}>數量</th><th style={thStyle}>狀態</th></tr></thead>
            <tbody>
              {transferList.map((t) => (
                <tr key={t.id}>
                  <td style={cellStyle}>{t.itemName}</td>
                  <td style={cellStyle}>{branchName(t.fromBranchId)} → {branchName(t.toBranchId)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.qty}</td>
                  <td style={cellStyle}>{t.status === 'BLOCKED' ? <Pill bg="var(--danger-bg)" color="var(--danger)">已阻擋{t.crossBorder ? '·跨境' : ''}</Pill> : t.status === 'IN_TRANSIT' ? <Pill bg="var(--warn-bg)" color="var(--warn)">運送中</Pill> : <Pill bg="var(--success-bg)" color="var(--success)">已送達</Pill>}</td>
                </tr>
              ))}
              {transferList.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>尚無調撥紀錄</td></tr>}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}

function ScopeChip({ active, onClick, label, sub }: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        borderRadius: 10,
        border: `1px solid ${active ? 'var(--primary)' : 'var(--line)'}`,
        background: active ? 'var(--primary-tint)' : 'var(--surface)',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 12.5, fontWeight: 600, color: active ? 'var(--primary-deep)' : 'var(--ink)' }}>{label}</div>
      <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{sub}</div>
    </button>
  );
}

function CatTab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 999,
        fontSize: 11.5,
        border: `1px solid ${active ? 'var(--primary)' : 'var(--line)'}`,
        background: active ? 'var(--primary)' : 'var(--surface)',
        color: active ? '#fff' : 'var(--ink-2)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function TransferModal({
  item,
  branches,
  branchOf,
  onClose,
  onDone,
}: {
  item: InventoryItem;
  branches: Branch[];
  branchOf: (id: string) => Branch | undefined;
  onClose: () => void;
  onDone: (blocked: boolean, error?: unknown) => void;
}) {
  const [toBranchId, setToBranchId] = useState('');
  const [qty, setQty] = useState(1);
  const from = branchOf(item.branchId);
  const to = branchOf(toBranchId);
  const crossBorder = !!to && !!from && from.country !== to.country;
  const enough = item.qty >= qty;

  const transfer = useMutation({
    mutationFn: () => api.post('/inventory/transfers', { itemName: item.name, fromBranchId: item.branchId, toBranchId, qty: Number(qty) }),
    onSuccess: () => onDone(false),
    // 僅 409（業務阻擋：跨境/庫存不足）視為「已阻擋並記錄」；其餘錯誤照實呈現
    onError: (e) => onDone(e instanceof ApiError && e.status === 409, e instanceof ApiError && e.status === 409 ? undefined : e),
  });

  return (
    <Modal open onClose={onClose} width={460}>
      <ModalHeader title="申請耗材調撥" sub={`${item.name}${item.spec ? ' · ' + item.spec : ''}`} onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ flex: 1, padding: 10, background: 'var(--surface-2)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>從（FROM）</div>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>{flagOf(from?.country ?? '')} {from?.name}</div>
            <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>當前庫存 {item.qty} {item.unit}</div>
          </div>
          <Icon d={I.arrow} size={16} style={{ color: 'var(--ink-3)', marginBottom: 12 }} />
          <div style={{ flex: 1 }}>
            <Field label="送往（TO）">
              <Select value={toBranchId} onChange={setToBranchId} placeholder="選擇分店" options={branches.filter((b) => b.id !== item.branchId).map((b) => ({ value: b.id, label: `${flagOf(b.country)} ${b.name}` }))} />
            </Field>
          </div>
        </div>
        <Field label="調撥數量">
          <input type="number" style={inputStyle} className="num" value={qty} onChange={(e) => setQty(Number(e.target.value))} />
        </Field>
        {to && (
          <div style={{ padding: 10, background: 'var(--surface-2)', borderRadius: 8, fontSize: 11.5 }}>
            調撥後　{from?.name}: <b className="num">{item.qty - qty}</b> {item.unit}　→　{to.name}: <b className="num">+{qty}</b>（保留原批號 / 效期）
          </div>
        )}
        {crossBorder ? (
          <div style={{ padding: 10, background: 'var(--danger-bg)', borderRadius: 8, fontSize: 11, color: 'var(--danger)' }}>
            <b>跨境硬阻擋：</b>{from?.country} → {to?.country} 屬不同關區/藥證持有人，無法直接調撥。
            <div style={{ marginTop: 4 }}><b style={{ color: 'var(--primary)' }}>系統建議：</b>改由 {to?.region} 當地註冊代理商發起獨立採購。</div>
          </div>
        ) : !enough && to ? (
          <div style={{ padding: 10, background: 'var(--warn-bg)', borderRadius: 8, fontSize: 11, color: 'var(--warn)' }}>來源庫存不足，無法調撥此數量。</div>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!toBranchId || qty < 1 || transfer.isPending} onClick={() => transfer.mutate()}>
          {crossBorder ? '送出（將被系統阻擋並記錄）' : '確認調撥'}
        </button>
      </div>
    </Modal>
  );
}

function AddItemModal({
  branches,
  treatments,
  existingCategories,
  onClose,
  onSaved,
  onFail,
}: {
  branches: Branch[];
  treatments: Treatment[];
  existingCategories: string[];
  onClose: () => void;
  onSaved: () => void;
  onFail: (e: unknown) => void;
}) {
  const [f, setF] = useState({ branchId: '', name: '', category: '', spec: '', brand: '', unit: '支', qty: 0, lowAt: 5, costYuan: 0, lot: '', expireAt: '', bindTreatmentId: '' });
  const set = (k: keyof typeof f, v: string | number) => setF((s) => ({ ...s, [k]: v }));

  const create = useMutation({
    mutationFn: () =>
      api.post('/inventory/items', {
        branchId: f.branchId,
        name: f.name,
        category: f.category || undefined,
        spec: f.spec || undefined,
        brand: f.brand || undefined,
        unit: f.unit || undefined,
        qty: Number(f.qty),
        lowAt: Number(f.lowAt),
        unitCostCents: Math.round(Number(f.costYuan) * 100),
        lot: f.lot || undefined,
        expireAt: f.expireAt ? new Date(f.expireAt).toISOString() : undefined,
        bindTreatmentId: f.bindTreatmentId || undefined,
      }),
    onSuccess: onSaved,
    onError: onFail,
  });

  return (
    <Modal open onClose={onClose} width={560}>
      <ModalHeader title="新增耗材品項" sub="規格 / 批號 / 效期 / 綁定療程" onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="分店 *">
            <Select value={f.branchId} onChange={(v) => set('branchId', v)} placeholder="選擇分店" options={branches.map((b) => ({ value: b.id, label: `${flagOf(b.country)} ${b.name}` }))} />
          </Field>
          <Field label="分類">
            <input style={inputStyle} list="cat-list" value={f.category} onChange={(e) => set('category', e.target.value)} placeholder="注射針劑 / 雷射探頭 / 麻藥…" />
            <datalist id="cat-list">{existingCategories.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <Field label="品名 *"><input style={inputStyle} value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="皮秒雷射探頭" /></Field>
          <Field label="品牌"><input style={inputStyle} value={f.brand} onChange={(e) => set('brand', e.target.value)} placeholder="PicoSure" /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <Field label="規格"><input style={inputStyle} value={f.spec} onChange={(e) => set('spec', e.target.value)} placeholder="755nm / 盒裝 10 入" /></Field>
          <Field label="單位"><input style={inputStyle} value={f.unit} onChange={(e) => set('unit', e.target.value)} placeholder="支 / 盒" /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <Field label="數量"><input type="number" style={inputStyle} className="num" value={f.qty} onChange={(e) => set('qty', Number(e.target.value))} /></Field>
          <Field label="低水位"><input type="number" style={inputStyle} className="num" value={f.lowAt} onChange={(e) => set('lowAt', Number(e.target.value))} /></Field>
          <Field label="單位成本（元）"><input type="number" style={inputStyle} className="num" value={f.costYuan} onChange={(e) => set('costYuan', Number(e.target.value))} /></Field>
          <Field label="效期"><input type="date" style={inputStyle} value={f.expireAt} onChange={(e) => set('expireAt', e.target.value)} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="批號"><input style={inputStyle} className="num" value={f.lot} onChange={(e) => set('lot', e.target.value)} placeholder="LOT-2026A" /></Field>
          <Field label="綁定療程（自動扣料）">
            <Select value={f.bindTreatmentId} onChange={(v) => set('bindTreatmentId', v)} placeholder="不綁定" options={treatments.map((t) => ({ value: t.id, label: t.name }))} />
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!f.branchId || !f.name || create.isPending} onClick={() => create.mutate()}>建立品項</button>
      </div>
    </Modal>
  );
}

function DeductionRulesCard({
  isAdmin,
  rules,
  treatments,
  treatmentName,
  onCreated,
  ok,
  fail,
}: {
  isAdmin: boolean;
  rules: { id: string; treatmentId: string; itemName: string; qty: number }[];
  treatments: Treatment[];
  treatmentName: (id: string | null) => string;
  onCreated: () => void;
  ok: (m: string) => void;
  fail: (e: unknown) => void;
}) {
  const [rule, setRule] = useState({ treatmentId: '', itemName: '', qty: 1 });
  const createRule = useMutation({
    mutationFn: () => api.post('/inventory/deduction-rules', { treatmentId: rule.treatmentId, itemName: rule.itemName, qty: Number(rule.qty) }),
    onSuccess: () => { ok('扣料規則已建立'); setRule({ treatmentId: '', itemName: '', qty: 1 }); onCreated(); },
    onError: fail,
  });

  return (
    <Card title="療程 ⇄ 耗材 · 自動扣減規則" icon={<Icon d={I.refresh} size={14} style={{ color: 'var(--primary)' }} />}>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <Select value={rule.treatmentId} onChange={(v) => setRule((s) => ({ ...s, treatmentId: v }))} placeholder="— 綁定療程 —" options={treatments.map((t) => ({ value: t.id, label: t.name }))} />
          <input style={fieldStyle} placeholder="耗材品名" value={rule.itemName} onChange={(e) => setRule((s) => ({ ...s, itemName: e.target.value }))} />
          <input type="number" style={{ ...fieldStyle, width: 90 }} placeholder="每次扣量" value={rule.qty} onChange={(e) => setRule((s) => ({ ...s, qty: Number(e.target.value) }))} />
          <button className="btn btn-primary" disabled={!rule.treatmentId || !rule.itemName || rule.qty < 1 || createRule.isPending} onClick={() => createRule.mutate()}>建立規則</button>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={thStyle}>療程</th><th style={thStyle}>耗材品名</th><th style={{ ...thStyle, textAlign: 'right' }}>每完工扣減</th></tr></thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.id}>
              <td style={cellStyle}>{treatmentName(r.treatmentId)}</td>
              <td style={cellStyle}>{r.itemName}</td>
              <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{r.qty}</td>
            </tr>
          ))}
          {rules.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={3}>尚無扣料規則</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
